import type {CreateExtArgs, CreateExtensionArgument, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    BuilderPresets,
    BuilderStyle,
    CompRef,
    Experiment,
    InnerElementPath,
    NestedDataPathSegment,
    OverrideOrDefaultData,
    Pointer
} from '@wix/document-services-types'
import type {
    Archetype,
    CssCustomPropertyItem,
    CssPropertyEnumCssPropertyType,
    CssPropertyItem,
    PresetInnerElementDefaults,
    ContentFill,
    SizingType,
    ComponentInitialSize
} from '@wix/ambassador-devcenter-v1-component-type-data/types'
import type {PlatformExtensionAPI} from '../platform'
import type {StructureExtensionAPI} from '../structure'
import type {InnerElementsExtensionAPI} from './innerElements'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {PresetsExtensionAPI} from '../presets'
import {DATA_TYPES} from '../../constants/constants'
import {
    ARCHETYPE,
    type CssCustomProperties,
    type CssProperties,
    type EditorReactComponent,
    type ElementItem,
    LAYOUT
    // eslint-disable-next-line wix-editor/no-internal-import
} from '@wix/component-protocol/schema'
import _ from 'lodash'
import type {TPAExtensionAPI} from '../tpa'
import {MAIN_TEXT_FIELD_MAPPING} from './mainTextFieldMapping'

interface ManifestApi extends ExtensionAPI {
    getArchType(componentType: string): Archetype
    getDisplayName(componentType: string): string
    getData(
        compPointer: Pointer,
        withInnerElements: boolean,
        types?: {prop: string; extract?(data: any): any}[]
    ): OverrideOrDefaultData[]
    getStyle(
        compPointer: Pointer,
        withInnerElements: boolean,
        types?: {prop: string; extract?(data: any): any}[]
    ): OverrideOrDefaultData[]
    innerElements: {
        buildTraversableFlatMap(compPointer: CompRef): Record<string, {innerElements: string[]}>
    }
    cssStyle: {
        getDefaultDefinition(
            compPointer: CompRef,
            innerElementsPath?: string[]
        ): Readonly<Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'>>
        getCustomCssPropertiesRoles(
            compPointer: CompRef,
            innerElementsPath?: string[]
        ): Readonly<Record<string, CssPropertyEnumCssPropertyType>>
    }
    validateContainerTypeForPopulation(
        slottedComponent: Pointer,
        slotName: string,
        innerElementPath?: InnerElementPath
    ): void
    layout: {
        isContentHeight(compPointer: CompRef): boolean
        isContentWidth(compPointer: CompRef): boolean
        shouldPreserveAspectRatio(compPointer: CompRef): boolean
        getContentFill(compPointer: CompRef): ContentFill | undefined
        getInitialSize(compPointer: CompRef): ComponentInitialSize | undefined
    }
    plugins: {
        hasOverlappingContainerPlugin(compPointer: CompRef): boolean
    }
    getMainTextField(compType: string, data: any): string | undefined
}

interface ManifestExtensionAPI extends ExtensionAPI {
    manifest: ManifestApi
}

interface ManifestMetaData {
    dataType: 'data' | 'cssProperties' | 'cssCustomProperties'
    namespace: 'data' | 'style'
}

type ExtractDataFunction = (
    override: any,
    manifestData: Record<string, any>,
    presetData: any,
    types: {prop: string; extract?(data: any): any}[] | undefined,
    isInnerElement: boolean,
    experimentInstance?: Experiment
) => OverrideOrDefaultData[]

const dataExtractDataFunction = (
    override: any,
    manifestData: Record<string, any>,
    typeNameInManifest: string,
    whereToWriteInDM: string,
    presetData: any,
    types: {prop: string; extract?(data: any): any}[] | undefined,
    isInnerElement: boolean | undefined,
    nestedDataPath: NestedDataPathSegment[] = []
) => {
    const getValue = (prop: string, overrideOrDefault: any, meta: any, defaultValue?: any) => {
        return overrideOrDefault?.[prop] ?? meta?.defaultValue ?? defaultValue
    }

    const getId = (overrideOrDefault: any, key: string, index?: number) => {
        const idField = '_id'
        const overrideOfProp = overrideOrDefault?.[key]
        return index !== undefined ? overrideOfProp?.[index]?.[idField] : overrideOfProp?.[idField]
    }

    const buildNestedPath = (
        nestedPath: NestedDataPathSegment[],
        type: 'array' | 'simple',
        key: string,
        overrideOrDefault: any,
        index?: number
    ): NestedDataPathSegment[] => {
        return [
            ...nestedPath,
            {
                type,
                key,
                _id: getId(overrideOrDefault, key, index)
            }
        ]
    }

    const extractNestedTypesData = (
        prop: string,
        meta: any,
        overrideOrDefault: any,
        presetDataNested: any,
        nestedPath: NestedDataPathSegment[]
    ): OverrideOrDefaultData[] => {
        const nestedResults: OverrideOrDefaultData[] = []
        const type = meta[typeNameInManifest]

        if (type === 'arrayItems' && meta.arrayItems?.data?.items) {
            const arrayValue = getValue(prop, overrideOrDefault, meta, [])
            const arrayPresetData = presetDataNested?.[prop] ?? presetDataNested?.[typeNameInManifest]?.[prop]

            if (Array.isArray(arrayValue)) {
                arrayValue.forEach((arrayItem: any, index: number) => {
                    const currentNestedPath = buildNestedPath(nestedPath, 'array', prop, overrideOrDefault, index)
                    const arrayItemPresetData = arrayPresetData?.[index] ?? arrayPresetData?.defaultValue?.[index]
                    const nestedResultsForItem = dataExtractDataFunction(
                        arrayItem,
                        meta.arrayItems.data.items,
                        typeNameInManifest,
                        whereToWriteInDM,
                        arrayItemPresetData,
                        types,
                        isInnerElement,
                        currentNestedPath
                    )
                    nestedResults.push(...nestedResultsForItem)
                })
            }
        }

        if (type === 'data' && meta.data?.items) {
            const nestedDataValue = getValue(prop, overrideOrDefault, meta, {})
            const currentNestedPath = buildNestedPath(nestedPath, 'simple', prop, overrideOrDefault)
            const nestedPresetData = presetDataNested?.[prop] ?? presetDataNested?.data?.[prop]
            const nestedResultsForData = dataExtractDataFunction(
                nestedDataValue,
                meta.data.items,
                typeNameInManifest,
                whereToWriteInDM,
                nestedPresetData,
                types,
                isInnerElement,
                currentNestedPath
            )
            nestedResults.push(...nestedResultsForData)
        }

        return nestedResults
    }

    const overrideOrDefaultDataArr: OverrideOrDefaultData[] = []
    Object.entries(manifestData).forEach(([prop, meta]: [string, any]) => {
        const type = meta[typeNameInManifest]
        const extractionDetails = types?.find(toExtractType => toExtractType.prop === type)

        if (type === 'arrayItems' || type === 'data') {
            const nestedResults = extractNestedTypesData(prop, meta, override, presetData, nestedDataPath)
            overrideOrDefaultDataArr.push(...nestedResults)
        }

        if (!types || extractionDetails) {
            const defaultValue = presetData?.[typeNameInManifest]?.[prop]?.defaultValue ?? meta?.defaultValue
            const value = getValue(prop, override, meta, defaultValue)
            overrideOrDefaultDataArr.push({
                prop,
                value: extractionDetails?.extract ? extractionDetails.extract(value) : value,
                type,
                whereToWriteInDM,
                metaData: meta[type] ?? {},
                nestedDataPath
            })
        }
    })
    return overrideOrDefaultDataArr
}

const typeToExtractDataFunction: Record<string, ExtractDataFunction> = {
    data: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined,
        isInnerElement: boolean
    ) => dataExtractDataFunction(override, manifestData, 'dataType', 'data', presetData, types, isInnerElement),
    cssProperties: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => {
        const whereToWriteInDM = 'cssProperties'
        if (!types) {
            return Object.entries(manifestData).map(([prop, meta]: [string, any]) => {
                const defaultValue = presetData?.[whereToWriteInDM]?.[prop]?.defaultValue ?? meta?.defaultValue
                const value = override[whereToWriteInDM]?.[prop] ?? defaultValue
                return {
                    value,
                    whereToWriteInDM,
                    prop,
                    type: prop,
                    metaData: meta?.[prop] ?? {}
                }
            })
        }
        return types
            .map(typeToExtract => {
                const {prop, extract} = typeToExtract
                const defaultValue =
                    presetData?.[whereToWriteInDM]?.[prop]?.defaultValue ?? manifestData?.[prop]?.defaultValue
                const value = override[whereToWriteInDM]?.[prop] ?? defaultValue
                const meta = manifestData?.[prop]
                return {
                    value: extract ? extract(value) : value,
                    whereToWriteInDM,
                    prop,
                    type: prop,
                    metaData: meta?.[prop] ?? {}
                }
            })
            .filter(context => context.value !== undefined)
    },
    cssCustomProperties: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => {
        const overrideOrDefaultDataArr: OverrideOrDefaultData[] = []
        Object.entries(manifestData).forEach(([prop, meta]: [string, any]) => {
            const type = meta.cssPropertyType
            const extractionDetails = types?.find(toExtractType => toExtractType.prop === type)
            if (!types || extractionDetails) {
                const defaultValue = presetData?.cssPropertyType?.[prop]?.defaultValue ?? meta?.defaultValue
                const value = override?.cssCustomProperties?.[prop] ?? defaultValue
                overrideOrDefaultDataArr.push({
                    prop,
                    value: extractionDetails?.extract ? extractionDetails.extract(value) : value,
                    type,
                    whereToWriteInDM: 'cssCustomProperties',
                    metaData: meta[type] ?? {}
                })
            }
        })
        return overrideOrDefaultDataArr
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    return {
        name: 'manifest',
        dependencies: new Set(['platform', 'structure', 'innerElements', 'variants', 'presets']),
        createExtensionAPI(extArgs: CreateExtArgs): ManifestExtensionAPI {
            const {extensionAPI, dal, pointers} = extArgs
            const {platform} = extensionAPI as PlatformExtensionAPI
            const {components} = extensionAPI as StructureExtensionAPI
            const {variants} = extensionAPI as VariantsExtensionAPI
            const {tpa} = extensionAPI as TPAExtensionAPI

            const getComponentDetailsFromCSM = (componentType: string): any => {
                const builderCompData = platform.getBuilderCompAppData(componentType)
                if (!builderCompData) return undefined
                const {appDefinitionId, componentId} = builderCompData
                const appPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
                const appData = dal.get(appPointer)
                const {data} = appData.components.find((comp: any) => comp.componentId === componentId)
                return data
            }

            const getOverridesOrDefaultFromType = (
                manifestData: Record<string, any>,
                override: any,
                manifestMetaData: ManifestMetaData,
                presetData: any,
                types: {prop: string; extract?(data: any): any}[] | undefined,
                isInnerElement: boolean
            ) => {
                return typeToExtractDataFunction[manifestMetaData.dataType](
                    override,
                    manifestData,
                    presetData,
                    types,
                    isInnerElement
                )
            }

            const getManifestDataElementsAndPresets = (
                compPointer: Pointer,
                manifestMetaData: ManifestMetaData
            ): {manifestData: any; elements: any; compPresets: any} => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType)
                const {[manifestMetaData.dataType]: manifestData, elements, presets: compPresets} = editorElement
                return {manifestData, elements, compPresets}
            }

            const getComponentOverridesOrDefaultFromType = (
                compPointer: Pointer,
                manifestMetaData: ManifestMetaData,
                withInnerElements: boolean = false,
                types?: {prop: string; extract?(data: any): any}[]
            ): OverrideOrDefaultData[] => {
                const {innerElements} = extensionAPI as InnerElementsExtensionAPI
                const {presets} = extensionAPI as PresetsExtensionAPI

                const flattenPresets = (compPresets: any) => {
                    if (!compPresets) return {}
                    return Object.fromEntries(
                        Object.entries(compPresets).map(([name, data]: [string, any]) => [
                            name,
                            data?.elements || data?.presetDefaults?.elements
                        ])
                    )
                }

                const getInnerElementsPreset = (
                    componentPointer: Pointer,
                    path: string[]
                ): BuilderPresets | undefined => {
                    return (
                        innerElements.getInnerElementPresets(componentPointer as CompRef, path) ||
                        presets.get(componentPointer as CompRef)
                    )
                }

                const getOverridesOrDefaultFromInnerElements = (
                    componentPointer: Pointer,
                    elements: Record<string, any>,
                    relativePath: string[],
                    overrideOrDefaultDataArr: OverrideOrDefaultData[],
                    compPresets: any,
                    requestedTypes: {prop: string; extract?(data: any): any}[] | undefined
                ) => {
                    if (!elements) return overrideOrDefaultDataArr
                    Object.entries(elements).forEach(([elem, val]: [string, any]) => {
                        const path = [...relativePath, elem]
                        const resolved = val.inlineElement ?? val.refElement?.resolvedElement ?? val
                        const innerElementData = resolved?.[manifestMetaData.dataType]
                        if (innerElementData) {
                            const override =
                                manifestMetaData.namespace === DATA_TYPES.theme
                                    ? innerElements.getInnerElementStyle(componentPointer as CompRef, path)
                                    : innerElements.getInnerElementData(componentPointer as CompRef, path)
                            const currentPreset = getInnerElementsPreset(componentPointer, path)
                            const presetData = currentPreset && compPresets?.[currentPreset.presetName]?.[elem]
                            overrideOrDefaultDataArr.push(
                                ...getOverridesOrDefaultFromType(
                                    innerElementData,
                                    override ?? {},
                                    manifestMetaData,
                                    presetData,
                                    requestedTypes,
                                    true
                                ).map(context => ({...context, path}))
                            )
                        }
                        getOverridesOrDefaultFromInnerElements(
                            componentPointer,
                            resolved?.elements,
                            [...relativePath, elem],
                            overrideOrDefaultDataArr,
                            flattenPresets(compPresets),
                            requestedTypes
                        )
                    })
                    return overrideOrDefaultDataArr
                }
                const {manifestData, elements, compPresets} = getManifestDataElementsAndPresets(
                    compPointer,
                    manifestMetaData
                )
                if (!manifestData)
                    return withInnerElements
                        ? getOverridesOrDefaultFromInnerElements(compPointer, elements, [], [], compPresets, types)
                        : []
                const item = variants.getComponentItemConsideringVariants(compPointer, manifestMetaData.namespace)
                const compOverrides = item ?? {}
                const currentPreset = presets?.get(compPointer as CompRef)
                const presetData = currentPreset && compPresets?.[currentPreset.presetName]?.presetDefaults
                const elementPropsArray = getOverridesOrDefaultFromType(
                    manifestData,
                    compOverrides,
                    manifestMetaData,
                    presetData,
                    types,
                    false
                )

                return withInnerElements
                    ? [
                          ...elementPropsArray,
                          ...getOverridesOrDefaultFromInnerElements(
                              compPointer,
                              elements,
                              [],
                              [],
                              flattenPresets(compPresets),
                              types
                          )
                      ]
                    : elementPropsArray
            }

            const extractProps = <
                T extends CssProperties | CssCustomProperties,
                K extends keyof (T extends CssCustomPropertyItem ? CssCustomPropertyItem : CssPropertyItem)
            >(
                obj: T,
                propertyName: K
            ): Readonly<
                Record<string, (T extends CssCustomPropertyItem ? CssCustomPropertyItem : CssPropertyItem)[K]>
            > => {
                return Object.freeze(
                    Object.fromEntries(
                        Object.entries(obj ?? {}).map(([key, value]) => [key, (value as any)[propertyName]])
                    )
                )
            }

            const inflateInnerElementsPath = (
                innerElementsPath: string[] = [],
                prefix = ['elements'],
                suffix = ['inlineElement']
            ): string[] => innerElementsPath.flatMap((element: string) => [...prefix, element, ...suffix])

            const getCustomCssPropertiesRoles = (
                compPointer: CompRef,
                innerElementsPath?: string[]
            ): Readonly<Record<string, CssPropertyEnumCssPropertyType>> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const path: string[] = inflateInnerElementsPath(innerElementsPath)

                return extractProps<CssCustomProperties, keyof CssCustomPropertyItem>(
                    _.get(editorElement, [...path, 'cssCustomProperties'], {}) satisfies CssCustomProperties,
                    'cssPropertyType'
                )
            }

            const getDefaultAccordingToAppliedPresets = (
                compPointer: CompRef,
                innerElementsPath: string[] = []
            ): Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const {innerElements} = extensionAPI as InnerElementsExtensionAPI
                const {presets} = extensionAPI as PresetsExtensionAPI
                const defaultsFromPresets = []
                const compPreset = presets.get(compPointer)?.presetName
                const inflatedInnerElementPresetPath = inflateInnerElementsPath(innerElementsPath, ['elements'], [])
                const currentInnerElementPath = []

                if (compPreset) {
                    const {cssProperties, cssCustomProperties} = _.get(
                        editorElement,
                        ['presets', compPreset, 'presetDefaults', ...inflatedInnerElementPresetPath],
                        {}
                    ) satisfies PresetInnerElementDefaults
                    defaultsFromPresets.push({
                        cssProperties: extractProps(cssProperties, 'defaultValue'),
                        cssCustomProperties: extractProps(cssCustomProperties, 'defaultValue')
                    })
                }

                for (const innerElement of innerElementsPath) {
                    currentInnerElementPath.push(innerElement)
                    const inflatedInnerElementPath = inflateInnerElementsPath(currentInnerElementPath)
                    inflatedInnerElementPresetPath.splice(0, 2)
                    const elementPreset = innerElements.getInnerElementPresets(
                        compPointer,
                        currentInnerElementPath
                    )?.presetName

                    if (!elementPreset) {
                        continue
                    }

                    const {cssProperties, cssCustomProperties} = _.get(
                        editorElement,
                        [
                            ...inflatedInnerElementPath,
                            'presets',
                            elementPreset,
                            'presetDefaults',
                            ...inflatedInnerElementPresetPath
                        ],
                        {}
                    ) satisfies PresetInnerElementDefaults

                    defaultsFromPresets.push({
                        cssProperties: extractProps(cssProperties, 'defaultValue'),
                        cssCustomProperties: extractProps(cssCustomProperties, 'defaultValue')
                    })
                }

                return _.defaultsDeep({}, ...defaultsFromPresets)
            }

            const getDefaultDefinition = (
                compPointer: CompRef,
                innerElementsPath: string[] = []
            ): Readonly<Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'>> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const inflatedInnerElementsPath: string[] = inflateInnerElementsPath(innerElementsPath)
                const defaultsFromPresets = getDefaultAccordingToAppliedPresets(compPointer, innerElementsPath)
                const elementDefaults = {
                    cssProperties: extractProps(
                        _.get(
                            editorElement,
                            [...inflatedInnerElementsPath, 'cssProperties'],
                            {}
                        ) satisfies CssProperties,
                        'defaultValue'
                    ),
                    cssCustomProperties: extractProps(
                        _.get(
                            editorElement,
                            [...inflatedInnerElementsPath, 'cssCustomProperties'],
                            {}
                        ) satisfies CssCustomProperties,
                        'defaultValue'
                    )
                }
                const {cssProperties, cssCustomProperties} = _.defaultsDeep(
                    defaultsFromPresets,
                    elementDefaults
                ) satisfies Pick<BuilderStyle, 'cssCustomProperties' | 'cssProperties'>

                return Object.freeze({
                    cssProperties: Object.freeze(cssProperties),
                    cssCustomProperties: Object.freeze(cssCustomProperties)
                })
            }

            const buildTraversableFlatMap = (compPointer: CompRef): Record<string, {innerElements: string[]}> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const map: Record<string, {innerElements: string[]}> = {}

                const traverse = (current: Record<string, ElementItem> | ElementItem | undefined, path: string[]) => {
                    const currentPathKey = path.join('.')
                    map[currentPathKey] = {
                        innerElements: []
                    }

                    if (!current || typeof current !== 'object') {
                        return
                    }

                    for (const [key, value] of Object.entries(current)) {
                        const newPath = [...path, key]
                        const newPathKey = newPath.join('.')

                        map[currentPathKey].innerElements.push(newPathKey)

                        traverse(value?.inlineElement?.elements, newPath)
                    }
                }

                traverse(editorElement?.elements, [compPointer.id])
                return map
            }

            const validateContainerTypeForPopulation = (
                slottedComponent: Pointer,
                slotName: string,
                innerElementPath?: InnerElementPath
            ) => {
                const slottedComponentType = components.getComponentType(slottedComponent)
                if (
                    platform.getBuilderCompAppData(slottedComponentType) &&
                    !tpa.isTpaByCompType(slottedComponentType)
                ) {
                    const builderData = getComponentOverridesOrDefaultFromType(
                        slottedComponent,
                        {dataType: 'data', namespace: 'data'},
                        true,
                        [{prop: 'container'}]
                    ).find(({prop, metaData, path}: any) => {
                        const isInvalidContainerType = ['placeholder', 'template', 'simple'].includes(
                            metaData?.containerType
                        )
                        const isPropMatch = prop === slotName

                        if (innerElementPath) {
                            return isPropMatch && _.isEqual(path, innerElementPath) && isInvalidContainerType
                        }
                        return isPropMatch && isInvalidContainerType
                    })

                    if (builderData) {
                        throw new Error('Cannot populate to container types placeholder, template, or simple')
                    }
                }
            }

            const getSizingType = (compPointer: CompRef, prop: 'width' | 'height'): SizingType | undefined => {
                const {presets} = extensionAPI as PresetsExtensionAPI
                const currentPreset: BuilderPresets | undefined = presets.get(compPointer)
                const componentType = components.getComponentType(compPointer)
                const {installation, editorElement} = (getComponentDetailsFromCSM(componentType) ??
                    {}) as EditorReactComponent
                const presetName = currentPreset?.presetName ?? installation?.presets?.large
                const preset = presetName ? editorElement?.presets?.[presetName] : undefined

                return preset?.initialSize?.[prop]?.sizingType ?? installation?.initialSize?.[prop]?.sizingType
            }

            const isContentHeight = (compPointer: CompRef): boolean =>
                getSizingType(compPointer, 'height') === LAYOUT.SIZING_TYPE.content

            const isContentWidth = (compPointer: CompRef): boolean =>
                getSizingType(compPointer, 'width') === LAYOUT.SIZING_TYPE.content

            const shouldPreserveAspectRatio = (compPointer: CompRef): boolean => {
                const componentType = components.getComponentType(compPointer)
                const {installation} = (getComponentDetailsFromCSM(componentType) ?? {}) as EditorReactComponent

                return !!installation?.initialSize?.preserveAspectRatio
            }

            const getContentFill = (compPointer: CompRef): ContentFill | undefined => {
                const compType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(compType) ?? {}
                return editorElement.layout?.contentFill
            }

            const getInitialSize = (compPointer: CompRef): ComponentInitialSize | undefined => {
                const compType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(compType) ?? {}
                return editorElement?.installation?.initialSize
            }

            const hasOverlappingContainerPlugin = (compPointer: CompRef): boolean => {
                const compType = components.getComponentType(compPointer)
                const {editorElement} = (getComponentDetailsFromCSM(compType) ?? {}) as EditorReactComponent
                const {dataItemKey} = editorElement?.layout?.contentFill ?? {}

                return !!(dataItemKey && editorElement!.data![dataItemKey].dataType === 'container')
            }

            const getMainTextField = (componentType: string, data: any): string | undefined => {
                const mainTextProperty = MAIN_TEXT_FIELD_MAPPING[componentType]?.[0]
                if (mainTextProperty) {
                    const path = mainTextProperty.elementPath === 'root' ? [] : mainTextProperty.elementPath.split('.')
                    path.push(mainTextProperty.key)
                    const mainText = _.get(data, path)
                    if (mainText) {
                        return mainText.text ?? mainText
                    }
                }
            }

            return {
                manifest: {
                    getArchType(componentType: string): Archetype {
                        const {editorElement} = getComponentDetailsFromCSM(componentType) ?? {}
                        return editorElement?.archetype ?? ARCHETYPE.UNKNOWN_Archetype
                    },
                    getDisplayName(componentType: string) {
                        const {displayName} = getComponentDetailsFromCSM(componentType)?.editorElement ?? {}
                        return displayName
                    },
                    getStyle(
                        compPointer: Pointer,
                        withInnerElements: boolean = false,
                        types?: {prop: string; extract?(data: any): any}[]
                    ) {
                        return [
                            ...getComponentOverridesOrDefaultFromType(
                                compPointer,
                                {dataType: 'cssProperties', namespace: 'style'},
                                withInnerElements,
                                types
                            ),
                            ...getComponentOverridesOrDefaultFromType(
                                compPointer,
                                {dataType: 'cssCustomProperties', namespace: 'style'},
                                withInnerElements,
                                types
                            )
                        ]
                    },
                    getData(
                        compPointer: Pointer,
                        withInnerElements: boolean = false,
                        types?: {prop: string; extract?(data: any): any}[]
                    ) {
                        return getComponentOverridesOrDefaultFromType(
                            compPointer,
                            {dataType: 'data', namespace: 'data'},
                            withInnerElements,
                            types
                        )
                    },
                    cssStyle: {
                        getDefaultDefinition,
                        getCustomCssPropertiesRoles
                    },
                    innerElements: {
                        buildTraversableFlatMap
                    },
                    validateContainerTypeForPopulation,
                    layout: {
                        isContentWidth,
                        isContentHeight,
                        shouldPreserveAspectRatio,
                        getContentFill,
                        getInitialSize
                    },
                    plugins: {
                        hasOverlappingContainerPlugin
                    },
                    getMainTextField
                }
            }
        }
    }
}

export {createExtension, ManifestApi, ManifestExtensionAPI}
